import React, { MouseEventHandler, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { startCase } from 'lodash-es';

import Checkbox from 'shared/components/Checkbox';
import LabeledContent from 'shared/components/LabeledContent';

import DefaultTransformationOption from './DefaultTransformationOption';
import { OptionComponentProps, TransformationOptionListProps } from './types';

const LABELED_CONTENT_HEADER_CLASS_NAME = 'transformation-option-list__labeled-content-header';

const StyledOptionWrapper = styled.div`
  margin-top: 0.666666667rem;
`;

const StyledLabeledContent = styled(LabeledContent)<Pick<TransformationOptionListProps, 'hasSelectAll'>>`
  && {
    ${({ hasSelectAll }) => hasSelectAll && css`
      .${LABELED_CONTENT_HEADER_CLASS_NAME} {
        padding-bottom: 0.75rem;
        border-bottom: 1px dashed #33373d33;

        label {
          cursor: pointer;
          user-select: none;
        }
      }
    `}
  }
`;

const renderOptions = (
  options: TransformationOptionListProps['options'],
  selectedOptions: TransformationOptionListProps['selectedOptions'],
  onSelect: OptionComponentProps['onSelect'],
  OptionComponent: TransformationOptionListProps['optionComponent'] = DefaultTransformationOption,
  optionComponentExtraProps: TransformationOptionListProps['optionComponentExtraProps'] = {},
  optionWrapperClassName: TransformationOptionListProps['optionWrapperClassName'],
) => options.map((option) => (
  <StyledOptionWrapper className={optionWrapperClassName} key={option}>
    <OptionComponent
      option={option}
      isSelected={!!selectedOptions!.find((selectedOption) => selectedOption === option)}
      onSelect={onSelect}
      {...optionComponentExtraProps}
    />
  </StyledOptionWrapper>
));

const TransformationOptionList = ({
  className,
  contentContainerClassName,
  optionWrapperClassName,
  name,
  title,
  options,
  selectedOptions = [],
  onChange,
  optionComponent: OptionComponent,
  optionComponentExtraProps,
  hasSelectAll, // Toggleable select-all
  helpText,
}: TransformationOptionListProps) => {

  const handleOptionSelection = useCallback((option, isSelected) => {
    if (option) {
      const newSelectedOptions = isSelected
        ? selectedOptions.concat(option)
        : selectedOptions.filter((selectedOption) => selectedOption !== option);;

      onChange(newSelectedOptions, name);
    }
  }, [name, selectedOptions]);

  const allOptionsSelected = options.length === selectedOptions.length;

  const handleSelectAllToggle = () => {
    // Unselect All
    if (allOptionsSelected) {
       onChange([], name);
    }
    // Select All
    else {
      onChange(options, name);
    }
  }

  const labelText = title || startCase(name);

  return (
    <StyledLabeledContent
      className={className}
      headerClassName={LABELED_CONTENT_HEADER_CLASS_NAME}
      contentContainerClassName={contentContainerClassName}
      label={labelText}
      customLabel={hasSelectAll ? <LabelWithSelectAll isChecked={allOptionsSelected} label={labelText} handleClick={handleSelectAllToggle} /> : null }
      helpText={helpText}
      hasSelectAll={hasSelectAll}
    >
      {renderOptions(
        options,
        selectedOptions,
        handleOptionSelection,
        OptionComponent,
        optionComponentExtraProps,
        optionWrapperClassName,
      )}
    </StyledLabeledContent>
  );
};

const StyledLabelWithCheckbox = styled.div`
  display: inline-flex;
  align-items: center;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-right: 1rem;
  opacity: 0.8;
`;

const LabelWithSelectAll = ({
  handleClick,
  label,
  isChecked,
}: {
  handleClick: MouseEventHandler;
  label: string;
  isChecked: boolean;
}) => {
  return (
    <StyledLabelWithCheckbox onClick={handleClick}>
      <StyledCheckbox label={label} isChecked={isChecked} />
    </StyledLabelWithCheckbox>
  );
};

export default TransformationOptionList;
